import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api";
import { useNotification } from "~hooks/useNotification";
import { INVALIDATE_PERMISSIONS_SCOPE_CACHE_KEY } from "~constants/permissions";

export const useFetchLeague = () => {
  const { fetchData } = useApi();
  const { organizationId, sport, dataId } = useParams();

  return useQuery({
    queryKey: ["leagues", organizationId, dataId],
    queryFn: async () => {
      const result = await fetchData({
        url: `${sport}/o/${organizationId}/leagues/${dataId}`,
      });
      return result?.data?.[0] || [];
    },
    enabled: !!dataId,
  });
};

export const useFetchLeagues = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport } = useParams();
  let URL = `${sport}/o/${organizationId}/leagues`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["leagues", organizationId, JSON.stringify(params)],
    queryFn: async () => {
      return await fetchData({
        url: URL,
      });
    },
  });
};

export const useDeleteLeague = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();

  return useMutation({
    mutationFn: async (leagueId) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/leagues/${leagueId}`,
        method: "DELETE",
      });
    },
    onSuccess: async () => {
      await localStorage.setItem(INVALIDATE_PERMISSIONS_SCOPE_CACHE_KEY, "true");

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["leagues"] }),
        queryClient.invalidateQueries({ queryKey: ["permission-scope-current-user"] }),
      ]);
    },
  });
};

export const useCreateLeague = () => {
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      const result = await mutateData({
        method: "POST",
        url: `${sport}/o/${organizationId}/leagues`,
        data,
      });

      return result?.data;
    },
    onSuccess: async () => {
      notification.success({
        message: `${formatMessage({
          id: "league.create.success",
          defaultMessage: "League created successfully",
        })}`,
        placement: "bottomRight",
      });

      localStorage.setItem(INVALIDATE_PERMISSIONS_SCOPE_CACHE_KEY, "true");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["leagues"] }),
        queryClient.invalidateQueries({ queryKey: ["permission-scope-current-user"] }),
      ]);
    },
  });
};

export const useUpdateLeague = () => {
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const { sport, organizationId, dataId } = useParams();
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      const result = await mutateData({
        method: "PUT",
        url: `${sport}/o/${organizationId}/leagues/${dataId}`,
        data,
      });

      return result?.data;
    },
    onSuccess: async () => {
      notification.success({
        message: `${formatMessage({
          id: "league.update.success",
          defaultMessage: "League updated successfully",
        })}`,
        placement: "bottomRight",
      });

      localStorage.setItem(INVALIDATE_PERMISSIONS_SCOPE_CACHE_KEY, "true");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["leagues"] }),
        queryClient.invalidateQueries({ queryKey: ["permission-scope-current-user"] }),
      ]);
    },
  });
};
